import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Box, Folder, FolderPlus, Plus, Settings, Star } from "lucide-react";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../components/Button/Button";
import type { ContextMenuItem } from "../../components/Menu/ContextMenu";
import { Modal } from "../../components/Modal/Modal";
import { EmptyNote } from "./EmptyNote";
import { FolderNameInput } from "./FolderNameInput";
import { folderDragId, releasePointerFocus, resolveDrop } from "./folderMoves";
import { fill, type SidebarFolderHandlers, type SidebarFolderLabels, type SidebarNavSpaceFolder } from "./folderTypes";
import { NavRow } from "./NavRow";
import type { SidebarNavSpaceItem } from "./SidebarNav";
import { SidebarSpaceFolder } from "./SidebarSpaceFolder";
import { SidebarSpaceRow } from "./SidebarSpaceRow";

/**
 * The user's own folders (Slack-style), plus any space not in one, folders first. A space's own
 * grip drags it between folders (keyboard: focus the grip, Space, arrows, Space); every move is
 * also in its right-click / "..." menu. Folders reorder by their own grip, or with Move up/down
 * in their menu.
 */
export function SidebarSpaceFolders({
  spaces,
  looseSpaces,
  folders,
  handlers,
  activeSpaceKey,
  onOpenSpace,
  onTogglePinSpace,
  requestNewFolder,
  labels: l,
  starLabel,
  unpinLabel,
  showStar = true,
  onNewSpace,
  newSpaceLabel,
}: Readonly<{
  spaces: SidebarNavSpaceItem[];
  /** Not in any folder; shown alongside the folders themselves, after them. */
  looseSpaces: SidebarNavSpaceItem[];
  folders: SidebarNavSpaceFolder[];
  handlers: SidebarFolderHandlers;
  activeSpaceKey?: string;
  onOpenSpace: (key: string) => void;
  onTogglePinSpace: (key: string) => void;
  /** Bump this (e.g. from a header's own "+") to start the new-folder flow from outside. */
  requestNewFolder?: number;
  labels: SidebarFolderLabels;
  starLabel: string;
  unpinLabel: string;
  /** False leaves only the "..." button on a row's hover, with favoriting in its menu. */
  showStar?: boolean;
  /** With a label, the "No spaces yet" note gets a "New space" row under it. */
  onNewSpace?: () => void;
  newSpaceLabel?: string;
}>) {
  // "new": the "+ New folder" field; a space key: "Move to a new folder..." for that space.
  const [pendingOrder, setPendingOrder] = useState<{ spaceKey: string; folderKey: string; index: number } | null>(null);
  const [naming, setNaming] = useState<"new" | string | null>(null);
  const newFolderRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (naming) newFolderRef.current?.scrollIntoView({ block: "nearest" });
  }, [naming]);
  useEffect(() => {
    if (requestNewFolder) setNaming("new");
  }, [requestNewFolder]);
  const byKey = useMemo(() => new Map(spaces.map((sp) => [sp.key, sp])), [spaces]);

  const rows: Array<{ type: "folder"; folder: SidebarNavSpaceFolder } | { type: "space"; space: SidebarNavSpaceItem }> =
    [
      ...folders.map((folder) => ({ type: "folder" as const, folder })),
      ...looseSpaces.map((space) => ({ type: "space" as const, space })),
    ];

  // The row being dragged follows the pointer in an overlay, so it can leave its own folder's list.
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [, draggingKey] = String(draggingId ?? "").split(/:(.*)/s);
  const draggingSpace = draggingId?.startsWith("space:") ? byKey.get(draggingKey) : undefined;

  const sensors = useSensors(
    // A few pixels before a drag starts, so clicking a grip doesn't drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const nameOf = (id: string | number | undefined): string => {
    const [kind, key] = String(id ?? "").split(/:(.*)/s);
    if (kind === "space") return byKey.get(key)?.name ?? "";
    return folders.find((s) => s.key === key)?.name ?? "";
  };

  const onDragEnd = ({ active, over, activatorEvent }: DragEndEvent) => {
    setDraggingId(null);
    releasePointerFocus(activatorEvent);
    const drop = resolveDrop(folders, String(active.id), over ? String(over.id) : null);
    if (drop?.type === "reorderFolders") handlers.onReorderFolders(drop.folderKeys);
    if (drop?.type === "confirmCustomOrder") setPendingOrder(drop);
    if (drop?.type === "moveSpace") handlers.onMoveSpace(drop.spaceKey, drop.folderKey, drop.index);
  };

  // The new-folder field sits under the last folder.
  const nameInput = naming ? (
    <div ref={newFolderRef} className="pr-1 pl-3">
      <FolderNameInput
        label={l.folderName}
        placeholder={l.folderNamePlaceholder}
        onSave={(name) => {
          if (naming === "new") handlers.onCreateFolder(name);
          else handlers.onCreateFolderWithSpace(name, naming);
          setNaming(null);
        }}
        onCancel={() => setNaming(null)}
      />
    </div>
  ) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={({ active }) => setDraggingId(String(active.id))}
      onDragEnd={onDragEnd}
      onDragCancel={({ activatorEvent }) => {
        setDraggingId(null);
        releasePointerFocus(activatorEvent);
      }}
      accessibility={{
        screenReaderInstructions: { draggable: l.dragInstructions },
        announcements: {
          onDragStart: ({ active }) => fill(l.pickedUp, nameOf(active.id)),
          // Not "X is over X" when it hasn't moved yet: that would replace "Picked up X".
          onDragOver: ({ active, over }) =>
            over && over.id !== active.id ? fill(l.movedOver, nameOf(active.id), nameOf(over.id)) : undefined,
          onDragEnd: ({ active }) => fill(l.dropped, nameOf(active.id)),
          onDragCancel: ({ active }) => fill(l.cancelled, nameOf(active.id)),
        },
      }}
    >
      <SortableContext items={folders.map((s) => folderDragId(s.key))} strategy={verticalListSortingStrategy}>
        {spaces.length === 0 && !naming && (
          <>
            <EmptyNote icon={<Box />}>{l.noSpaces}</EmptyNote>
            {onNewSpace && newSpaceLabel && (
              <NavRow icon={<Plus />} onClick={onNewSpace}>
                {newSpaceLabel}
              </NavRow>
            )}
          </>
        )}
        {folders.length === 0 && nameInput}
        {rows.map((row, rowIndex) => {
          if (row.type === "folder") {
            const folderEl = (
              <SidebarSpaceFolder
                key={row.folder.key}
                folder={row.folder}
                folders={folders}
                spaces={byKey}
                activeSpaceKey={activeSpaceKey}
                onOpenSpace={onOpenSpace}
                onTogglePinSpace={onTogglePinSpace}
                onStartNewFolder={(spaceKey) => setNaming(spaceKey)}
                handlers={handlers}
                labels={l}
                starLabel={starLabel}
                unpinLabel={unpinLabel}
                showStar={showStar}
              />
            );
            return rowIndex === folders.length - 1 ? (
              <Fragment key={row.folder.key}>
                {folderEl}
                {nameInput}
              </Fragment>
            ) : (
              folderEl
            );
          }
          const space = row.space;
          const moveItems: ContextMenuItem[] = [
            ...(showStar
              ? []
              : [
                  {
                    label: space.pinned ? unpinLabel : starLabel,
                    icon: <Star size={14} />,
                    onSelect: () => onTogglePinSpace(space.key),
                  },
                ]),
            ...(handlers.onOpenSpaceSettings
              ? [
                  {
                    label: l.spaceSettings,
                    icon: <Settings size={14} />,
                    onSelect: () => handlers.onOpenSpaceSettings?.(space.key),
                  },
                ]
              : []),
            ...folders.map((s) => ({
              label: fill(l.moveTo, s.name),
              icon: <Folder size={14} />,
              onSelect: () => handlers.onMoveSpace(space.key, s.key, null),
            })),
            { label: l.moveToNewFolder, icon: <FolderPlus size={14} />, onSelect: () => setNaming(space.key) },
          ];
          return (
            <div key={space.key}>
              <SidebarSpaceRow
                space={space}
                showStar={showStar}
                active={space.key === activeSpaceKey}
                starLabel={space.pinned ? unpinLabel : starLabel}
                onOpen={() => onOpenSpace(space.key)}
                onToggleStar={() => onTogglePinSpace(space.key)}
                menu={{ label: l.spaceMenu, ariaLabel: fill(l.spaceMenuAria, space.name), items: moveItems }}
              />
            </div>
          );
        })}
      </SortableContext>
      <DragOverlay dropAnimation={null}>
        {draggingSpace && (
          <div className="rounded-lg bg-surface-elevated shadow-md">
            <SidebarSpaceRow
              space={draggingSpace}
              indented={folders.some((sec) => sec.spaceKeys.includes(draggingSpace.key))}
              active={draggingSpace.key === activeSpaceKey}
              starLabel={draggingSpace.pinned ? unpinLabel : starLabel}
              onOpen={() => {}}
              onToggleStar={() => {}}
            />
          </div>
        )}
      </DragOverlay>
      <Modal
        open={!!pendingOrder}
        onClose={() => setPendingOrder(null)}
        title={l.customOrderTitle}
        actions={
          <>
            <Button variant="secondaryBordered" onClick={() => setPendingOrder(null)}>
              {l.customOrderCancel}
            </Button>
            <Button
              onClick={() => {
                if (!pendingOrder) return;
                handlers.onSetFolderSort(pendingOrder.folderKey, "custom");
                handlers.onMoveSpace(pendingOrder.spaceKey, pendingOrder.folderKey, pendingOrder.index);
                setPendingOrder(null);
              }}
            >
              {l.customOrderConfirm}
            </Button>
          </>
        }
      >
        <p className="text-content-secondary [font:var(--type-body-sm)]">
          {fill(l.customOrderBody, folders.find((sec) => sec.key === pendingOrder?.folderKey)?.name ?? "")}
        </p>
      </Modal>
    </DndContext>
  );
}
